// client/src/utils/permissions.js

export const ROLES = {
  TECHNICIAN: 'technician',
  TEAM_LEAD: 'team_lead',
  PRODUCTION_MANAGER: 'production_manager',
  ENGINEERING_MANAGER: 'engineering_manager',
  PLANNER: 'planner',
  ENGINEER: 'engineer',
  ADMIN: 'admin'
};

export const PERMISSIONS = {
  VIEW_DASHBOARD: 'view_dashboard',
  VIEW_MACHINES: 'view_machines',
  MANAGE_MACHINES: 'manage_machines',
  MANAGE_USERS: 'manage_users',
  GENERATE_REPORTS: 'generate_reports',
  VIEW_TECHNICAL_DATA: 'view_technical_data',
  VIEW_MANAGEMENT_DATA: 'view_management_data',
  VIEW_PLANNING_DATA: 'view_planning_data',
  MANAGE_MOLDS: 'manage_molds',
  MANAGE_PARTS: 'manage_parts',
  VIEW_WORK_ORDERS: 'view_work_orders',
  MANAGE_WORK_ORDERS: 'manage_work_orders',
  VIEW_KAIZEN: 'view_kaizen',
  CREATE_KAIZEN: 'create_kaizen',
  VOTE_KAIZEN: 'vote_kaizen',
  APPROVE_KAIZEN: 'approve_kaizen',
  MANAGE_KAIZEN: 'manage_kaizen',
  VIEW_MOLD_SCHEDULE: 'view_mold_schedule',
  MANAGE_MOLD_SCHEDULE: 'manage_mold_schedule',
  VIEW_SIXS: 'view_sixs',
  CONDUCT_SIXS_AUDIT: 'conduct_sixs_audit',
  MANAGE_SIXS_AREAS: 'manage_sixs_areas',
  VIEW_SIXS_ANALYSIS: 'view_sixs_analysis',
  VIEW_ANALYTICS: 'view_analytics',
  ACCESS_ANALYTICS_DASHBOARD: 'access_analytics_dashboard',
  VIEW_SAFETY_AREAS: 'view_safety_areas',
  MANAGE_SAFETY_AREAS: 'manage_safety_areas',
  CONDUCT_SAFETY_INSPECTION: 'conduct_safety_inspection',
  CONDUCT_SAFETY_AUDIT: 'conduct_safety_audit',
  REPORT_SAFETY_INCIDENT: 'report_safety_incident',
  MANAGE_SAFETY_INCIDENTS: 'manage_safety_incidents',
  VIEW_SAFETY_ANALYTICS: 'view_safety_analytics',
  VIEW_PM_AREAS: 'view_pm_areas',
  MANAGE_PM_AREAS: 'manage_pm_areas',
  EXECUTE_PM_TASKS: 'execute_pm_tasks',
  VIEW_PM_ANALYTICS: 'view_pm_analytics'
};

export const ROLE_PERMISSIONS = {
  [ROLES.TECHNICIAN]: [
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.VIEW_MACHINES,
    PERMISSIONS.VIEW_TECHNICAL_DATA,
    PERMISSIONS.VIEW_KAIZEN,
    PERMISSIONS.CREATE_KAIZEN,
    PERMISSIONS.VOTE_KAIZEN,
    PERMISSIONS.VIEW_MOLD_SCHEDULE,
    PERMISSIONS.VIEW_SIXS,
    PERMISSIONS.VIEW_ANALYTICS,
    PERMISSIONS.ACCESS_ANALYTICS_DASHBOARD,
    PERMISSIONS.VIEW_SAFETY_AREAS,
    PERMISSIONS.CONDUCT_SAFETY_INSPECTION,
    PERMISSIONS.REPORT_SAFETY_INCIDENT,
    PERMISSIONS.VIEW_PM_AREAS,
    PERMISSIONS.EXECUTE_PM_TASKS
  ],
  [ROLES.TEAM_LEAD]: [
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.VIEW_MACHINES,
    PERMISSIONS.VIEW_TECHNICAL_DATA,
    PERMISSIONS.VIEW_MANAGEMENT_DATA,
    PERMISSIONS.VIEW_WORK_ORDERS,
    PERMISSIONS.VIEW_KAIZEN,
    PERMISSIONS.CREATE_KAIZEN,
    PERMISSIONS.VOTE_KAIZEN,
    PERMISSIONS.VIEW_MOLD_SCHEDULE,
    PERMISSIONS.VIEW_SIXS,
    PERMISSIONS.CONDUCT_SIXS_AUDIT,
    PERMISSIONS.VIEW_SIXS_ANALYSIS,
    PERMISSIONS.VIEW_ANALYTICS,
    PERMISSIONS.ACCESS_ANALYTICS_DASHBOARD,
    PERMISSIONS.VIEW_SAFETY_AREAS,
    PERMISSIONS.CONDUCT_SAFETY_INSPECTION,
    PERMISSIONS.CONDUCT_SAFETY_AUDIT,
    PERMISSIONS.REPORT_SAFETY_INCIDENT,
    PERMISSIONS.MANAGE_SAFETY_INCIDENTS,
    // Add PM permissions for team leads
    PERMISSIONS.VIEW_PM_AREAS,
    PERMISSIONS.EXECUTE_PM_TASKS,
    PERMISSIONS.VIEW_PM_ANALYTICS
  ],
  [ROLES.PRODUCTION_MANAGER]: [
    ...Object.values(PERMISSIONS)
  ],
  [ROLES.ENGINEERING_MANAGER]: [
    ...Object.values(PERMISSIONS)
  ],
  [ROLES.ENGINEER]: [
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.VIEW_MACHINES,
    PERMISSIONS.VIEW_TECHNICAL_DATA,
    PERMISSIONS.VIEW_MANAGEMENT_DATA,
    PERMISSIONS.VIEW_WORK_ORDERS,
    PERMISSIONS.VIEW_KAIZEN,
    PERMISSIONS.CREATE_KAIZEN,
    PERMISSIONS.VOTE_KAIZEN,
    PERMISSIONS.VIEW_MOLD_SCHEDULE,
    PERMISSIONS.VIEW_SIXS,
    PERMISSIONS.CONDUCT_SIXS_AUDIT,
    PERMISSIONS.MANAGE_SIXS_AREAS,
    PERMISSIONS.VIEW_SIXS_ANALYSIS,
    PERMISSIONS.VIEW_ANALYTICS,
    PERMISSIONS.ACCESS_ANALYTICS_DASHBOARD,
    PERMISSIONS.VIEW_SAFETY_AREAS,
    PERMISSIONS.CONDUCT_SAFETY_INSPECTION,
    PERMISSIONS.CONDUCT_SAFETY_AUDIT,
    PERMISSIONS.REPORT_SAFETY_INCIDENT,
    PERMISSIONS.MANAGE_SAFETY_INCIDENTS,
    PERMISSIONS.VIEW_PM_AREAS,
    PERMISSIONS.EXECUTE_PM_TASKS,
    PERMISSIONS.MANAGE_PM_AREAS,
    PERMISSIONS.VIEW_PM_ANALYTICS
  ],
  [ROLES.PLANNER]: [
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.VIEW_MACHINES,
    PERMISSIONS.MANAGE_MACHINES,
    PERMISSIONS.VIEW_PLANNING_DATA,
    PERMISSIONS.MANAGE_MOLDS,
    PERMISSIONS.MANAGE_PARTS,
    PERMISSIONS.VIEW_WORK_ORDERS,
    PERMISSIONS.MANAGE_WORK_ORDERS,
    PERMISSIONS.VIEW_KAIZEN,
    PERMISSIONS.CREATE_KAIZEN,
    PERMISSIONS.VOTE_KAIZEN,
    PERMISSIONS.VIEW_MOLD_SCHEDULE,
    PERMISSIONS.MANAGE_MOLD_SCHEDULE,
    PERMISSIONS.VIEW_SIXS,
    PERMISSIONS.VIEW_ANALYTICS,
    PERMISSIONS.ACCESS_ANALYTICS_DASHBOARD,
    PERMISSIONS.VIEW_PM_AREAS
  ],
  [ROLES.ADMIN]: Object.values(PERMISSIONS)
};

export const hasPermission = (userRole, permission) => {
  return ROLE_PERMISSIONS[userRole]?.includes(permission) || false;
};

export const hasAnyPermission = (userRole, permissions) => {
  return permissions.some(permission => hasPermission(userRole, permission));
};

export const hasAllPermissions = (userRole, permissions) => {
  return permissions.every(permission => hasPermission(userRole, permission));
};